import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Users, Plus, Pencil, Trash2, Car, Bike, Clock,
  BarChart3, Search, Loader2, UserCheck, Download, Upload
} from 'lucide-react';
import AppFooter from '../components/AppFooter';
import BackButton from '../components/BackButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dqftlupc_Recurso%2021VR%20logos.png";
export default function MembersPage() {
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showStats, setShowStats] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({ nombre: '', apellido: '', dni: '', plate: '', vehicle_type: 'auto' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [membersRes, statsRes] = await Promise.all([
        axios.get(`${API}/members`, { withCredentials: true }),
        axios.get(`${API}/members/stats`, { withCredentials: true })
      ]);
      setMembers(membersRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Error al cargar socios');
    } finally {
      setIsLoading(false);
    }
  };

  const openCreate = () => {
    setEditingMember(null);
    setForm({ nombre: '', apellido: '', dni: '', plate: '', vehicle_type: 'auto', notas: '', is_free: false });
    setShowModal(true);
  };

  const openEdit = (member) => {
    setEditingMember(member);
    setForm({
      nombre: member.nombre, apellido: member.apellido,
      dni: member.dni, plate: member.plate, vehicle_type: member.vehicle_type,
      notas: member.notas || '', is_free: member.is_free || false
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.nombre || !form.apellido || !form.dni || !form.plate) {
      toast.error('Completa todos los campos');
      return;
    }
    setIsSaving(true);
    try {
      if (editingMember) {
        await axios.put(`${API}/members/${editingMember.id}`, form, { withCredentials: true });
        toast.success('Socio actualizado');
      } else {
        await axios.post(`${API}/members`, form, { withCredentials: true });
        toast.success('Socio agregado');
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este socio?')) return;
    try {
      await axios.delete(`${API}/members/${id}`, { withCredentials: true });
      toast.success('Socio eliminado');
      fetchData();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/members/import/excel`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Importados: ${data.imported} socios`);
      if (data.errors.length > 0) {
        toast.warning(`${data.errors.length} errores: ${data.errors.slice(0, 3).join(', ')}`);
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al importar');
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const filtered = members.filter(m =>
    m.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.apellido.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.plate.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.dni.includes(searchTerm)
  );

  const getStatForMember = (id) => stats.find(s => s.id === id);

  const formatDuration = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col overflow-x-hidden">
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex min-h-14 items-center justify-between gap-3 py-2 sm:min-h-16">
            <div className="flex min-w-0 items-center gap-2 sm:gap-4">
              <BackButton to="/dashboard" />
              <div className="min-w-0 border-l border-slate-700 pl-2 sm:pl-4">
                <h1 className="text-base sm:text-lg font-bold leading-tight text-white">Socios</h1>
                <p className="text-xs text-slate-400">Gestión de socios del club</p>
              </div>
            </div>
            <img src={LOGO_URL} alt="ParkingVision AI" className="hidden h-8 w-auto shrink-0 sm:block" />
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-7xl flex-1 px-3 py-4 sm:px-6 sm:py-8 lg:px-8">
        {/* Top bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="relative flex-1 sm:w-64">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar..."
                className="bg-slate-800 border-slate-700 text-white pl-10" data-testid="search-members" />
            </div>
            <Badge className="bg-blue-600 whitespace-nowrap">{members.length}</Badge>
          </div>
          <div className="grid grid-cols-4 gap-2 sm:flex sm:flex-wrap">
            <a href={`${API}/members/export/excel`} data-testid="export-members-excel">
              <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:bg-slate-700">
                <Download className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Excel</span>
              </Button>
            </a>
            <input type="file" ref={fileInputRef} accept=".xlsx,.xls" onChange={handleImport} className="hidden" data-testid="import-members-input" />
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={isImporting}
              className="border-slate-700 text-slate-300 hover:bg-slate-700" data-testid="import-members-btn">
              {isImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 sm:mr-1" />}<span className="hidden sm:inline">Importar</span>
            </Button>
            <Button size="sm" onClick={() => setShowStats(!showStats)} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-700" data-testid="toggle-stats-btn">
              <BarChart3 className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">{showStats ? 'Ocultar' : 'Stats'}</span>
            </Button>
            <Button size="sm" onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-member-btn">
              <Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Agregar</span>
            </Button>
          </div>
        </div>

        {/* Members Table */}
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="text-center py-12 text-slate-400">Cargando...</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{searchTerm ? 'No se encontraron socios' : 'No hay socios registrados'}</p>
              </div>
            ) : (
              <>
              <div className="grid gap-3 p-3 md:hidden">
                {filtered.map((member) => {
                  const stat = getStatForMember(member.id);
                  return (
                    <article key={member.id} className="rounded-xl border border-slate-700 bg-slate-900/40 p-4" data-testid={`member-card-${member.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="break-words font-semibold text-white">{member.apellido}, {member.nombre}</p>
                          <p className="mt-1 text-sm text-slate-400">DNI {member.dni}</p>
                        </div>
                        <div className="flex shrink-0 gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(member)} className="h-9 w-9 p-0 text-blue-400" aria-label={`Editar ${member.nombre}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(member.id)} className="h-9 w-9 p-0 text-red-400" aria-label={`Eliminar ${member.nombre}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <span className="rounded bg-slate-700 px-2 py-1 font-mono font-bold text-white">{member.plate}</span>
                        <Badge variant="outline" className={member.vehicle_type === 'moto' ? 'text-amber-400 border-amber-400/50' : 'text-blue-400 border-blue-400/50'}>
                          {member.vehicle_type === 'moto' ? <Bike className="mr-1 h-3 w-3" /> : <Car className="mr-1 h-3 w-3" />}
                          {member.vehicle_type === 'moto' ? 'Moto' : 'Auto'}
                        </Badge>
                        {showStats && <Badge className={stat?.is_inside ? 'bg-emerald-600' : 'bg-slate-600'}>{stat?.is_inside ? 'Adentro' : 'Afuera'}</Badge>}
                      </div>
                      {showStats && (
                        <div className="mt-3 grid grid-cols-2 gap-2 border-t border-slate-700 pt-3 text-sm">
                          <div><span className="block text-xs text-slate-500">Visitas</span><span className="text-white">{stat?.total_visits || 0}</span></div>
                          <div><span className="block text-xs text-slate-500">Tiempo total</span><span className="text-white">{formatDuration(stat?.total_minutes || 0)}</span></div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Socio</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">DNI</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Patente</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Vehiculo</th>
                      {showStats && (
                        <>
                          <th className="text-center py-3 px-4 text-slate-400 font-medium">Visitas</th>
                          <th className="text-center py-3 px-4 text-slate-400 font-medium">Tiempo Total</th>
                          <th className="text-center py-3 px-4 text-slate-400 font-medium">Estado</th>
                        </>
                      )}
                      <th className="text-right py-3 px-4 text-slate-400 font-medium">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((member) => {
                      const stat = getStatForMember(member.id);
                      return (
                        <tr key={member.id} className="border-b border-slate-700/50 hover:bg-slate-700/30" data-testid={`member-row-${member.id}`}>
                          <td className="py-3 px-4">
                            <div>
                              <p className="text-white font-semibold">{member.apellido}, {member.nombre}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-slate-300">{member.dni}</td>
                          <td className="py-3 px-4">
                            <span className="font-mono font-bold text-white bg-slate-700 px-2 py-1 rounded">{member.plate}</span>
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant="outline" className={member.vehicle_type === 'moto' ? 'text-amber-400 border-amber-400/50' : 'text-blue-400 border-blue-400/50'}>
                              {member.vehicle_type === 'moto' ? <Bike className="w-3 h-3 mr-1" /> : <Car className="w-3 h-3 mr-1" />}
                              {member.vehicle_type === 'moto' ? 'Moto' : 'Auto'}
                            </Badge>
                          </td>
                          {showStats && (
                            <>
                              <td className="py-3 px-4 text-center text-white font-semibold">{stat?.total_visits || 0}</td>
                              <td className="py-3 px-4 text-center text-slate-300">{formatDuration(stat?.total_minutes || 0)}</td>
                              <td className="py-3 px-4 text-center">
                                {stat?.is_inside ? (
                                  <Badge className="bg-emerald-600">Adentro</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-slate-400 border-slate-600">Afuera</Badge>
                                )}
                              </td>
                            </>
                          )}
                          <td className="py-3 px-4 text-right">
                            <div className="flex gap-1 justify-end">
                              <Button variant="ghost" size="sm" onClick={() => openEdit(member)} className="text-blue-400 hover:text-blue-300 h-8 w-8 p-0" data-testid={`edit-member-${member.id}`}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(member.id)} className="text-red-400 hover:text-red-300 h-8 w-8 p-0" data-testid={`delete-member-${member.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <AppFooter />

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-h-[92dvh] max-w-md overflow-y-auto border-slate-700 bg-slate-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-emerald-400" />
              {editingMember ? 'Editar Socio' : 'Nuevo Socio'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-slate-300 text-sm">Nombre</Label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="member-nombre" />
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Apellido</Label>
                <Input value={form.apellido} onChange={(e) => setForm({ ...form, apellido: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="member-apellido" />
              </div>
            </div>
            <div>
              <Label className="text-slate-300 text-sm">DNI</Label>
              <Input value={form.dni} onChange={(e) => setForm({ ...form, dni: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="member-dni" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Patente</Label>
              <Input value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })}
                placeholder="ABC123" className="bg-slate-700 border-slate-600 text-white font-mono mt-1 uppercase" data-testid="member-plate" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Tipo de Vehiculo</Label>
              <Select value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="member-vehicle-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="auto">Auto</SelectItem>
                  <SelectItem value="moto">Moto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Notas (opcional)</Label>
              <Input value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })}
                placeholder="Observaciones sobre el socio..." className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="member-notas" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_free} onCheckedChange={(v) => setForm({ ...form, is_free: v })} data-testid="member-is-free" />
              <Label className="text-slate-300 text-sm">{form.is_free ? 'Socio FREE (no paga cuota)' : 'Paga cuota normal'}</Label>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1 border-slate-600 text-slate-300">Cancelar</Button>
              <Button onClick={handleSave} disabled={isSaving} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="save-member-btn">
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
